/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import humanize from 'humanize-plus';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import { Card, Tooltip } from '@mui/material';

import { Namespaces } from '../../i18n';
import { Dataset } from '../../types/dataset/Dataset';
import { useDatasetCardStyles } from './DatasetCard.styles';

export const dateFormat: string = 'dd.MM.yyyy';

export type DatasetCardProps = {
  className?: string;
  dataSet: Dataset;
  renderToUpper?: () => React.ReactNode;
  renderToLower?: () => React.ReactNode;
};

export function DatasetCard(props: DatasetCardProps) {
  const { className, dataSet, renderToUpper = () => null, renderToLower = () => null } = props;
  const { description, finalSizeMb, name, updatedAt, schema, importSizeMb } = dataSet;
  const classes = useDatasetCardStyles();
  const { t, i18n } = useTranslation([Namespaces.General]);

  const finalSize = useMemo(() => humanize.fileSize(+finalSizeMb * Math.pow(1024, 2)), [finalSizeMb]);

  const importSize = useMemo(() => humanize.fileSize(+importSizeMb * Math.pow(1024, 2)), [importSizeMb]);

  const formattedUpdatedAt = useMemo(
    () => DateTime.fromISO(updatedAt).setLocale(i18n.language).toFormat(dateFormat),
    [updatedAt, i18n.language],
  );

  return (
    <Card className={className} classes={{ root: classes.root }}>
      <div className={classes.upper}>
        <div className={classes.title}>{name}</div>
        {description && (
          <Tooltip
            title={description}
            classes={{
              tooltip: classes.tooltip,
              tooltipPlacementBottom: classes.tooltipPlacementBottom,
            }}
          >
            <div className={classes.titleLabel}>
              {t('labels.description')}
              &nbsp;
              <InfoIcon className={classes.infoIcon} />
            </div>
          </Tooltip>
        )}
        {schema && (
          <Tooltip
            title={schema}
            classes={{
              tooltip: classes.tooltip,
              tooltipPlacementBottom: classes.tooltipPlacementBottom,
            }}
          >
            <div className={classes.titleLabel}>
              {t('labels.schema')}
              &nbsp;
              <InfoIcon className={classes.infoIcon} />
            </div>
          </Tooltip>
        )}
        {renderToUpper()}
      </div>
      <div className={classes.lower}>
        <div className={classes.date}>{formattedUpdatedAt}</div>
        <div className={classes.size}>{t('dataset_card.size_info', { finalSize, importSize })}</div>
        {renderToLower()}
      </div>
    </Card>
  );
}
